import React, { useEffect, useState } from 'react';

import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import SelectDialog, {
  SelectDialogSearchField,
  SelectDialogSubmitButton,
  SelectDialogTable
} from '../../../common/dialogs/SelectDialog';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { Task } from '../../tasks/Task.interface';
import { useFindHazardReferenceTargets } from '../Hazard.hooks';
import { HazardReferenceTarget } from '../Hazard.interface';

export interface HazardAssignReferenceDialogProps {
  hazardId: number;
  target: HazardReferenceTarget;
  displayName: string;
  queryAlias: string;
  idPrefix: string;
  references: { id: number; title: string; task: Task }[];
  onCancel: () => void;
  onSubmit: (target: { id: number; title: string; task: Task }[]) => void;
}

const HazardAssignReferenceDialog: React.FC<
  HazardAssignReferenceDialogProps
> = ({
  hazardId,
  target,
  displayName,
  queryAlias,
  idPrefix,
  references,
  onCancel,
  onSubmit
}) => {
  const defaultLimit = 5;
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(defaultLimit);

  const {
    targetEntityList,
    isLoadingTargets,
    loadingTargetsError,
    findReferenceTargets
  } = useFindHazardReferenceTargets(hazardId, target);

  // handle searching
  useEffect(() => {
    findReferenceTargets({ search, page, limit });
  }, [search, limit, page, findReferenceTargets]);
  return (
    <SelectDialog open onClose={onCancel} selectedItems={references}>
      <DialogTitle>Assign {displayName}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Search for {displayName} to assign
        </DialogContentText>
        <SelectDialogSearchField
          label={displayName}
          onSearch={(value: string) => {
            setPage(0);
            setSearch(value);
          }}
        />
        <SelectDialogTable
          multiple
          idKey='id'
          columns={[
            {
              key: `id`,
              label: 'ID',
              render: item => `${idPrefix}-${item.id}`
            },
            { key: `title`, label: 'Title', wrap: true },
            {
              key: 'task.status',
              label: 'Status',
              render: item => {
                return item.task && <TaskStatusLabel task={item.task} />;
              }
            }
          ]}
          isLoading={isLoadingTargets}
          dataList={targetEntityList}
          page={page}
          rowsPerPage={limit}
          onChangePage={page => setPage(page)}
          onChangeRowsPerPage={limit => setLimit(limit)}
        />
        {loadingTargetsError && (
          <Alert severity='error'>
            Error getting data. Please check your internet connection and try
            again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <SelectDialogSubmitButton onSubmit={onSubmit}>
          Assign
        </SelectDialogSubmitButton>
      </DialogActions>
    </SelectDialog>
  );
};

export default HazardAssignReferenceDialog;
